import {Action, Selector, State, StateContext, Store} from "@ngxs/store";
import {CarteraStateInterface} from "../models/cartera-state.interface";
import {Injectable} from "@angular/core";
import {RequestService} from "../services/request.service";
import {AppHelper} from "../helpers/app.helper";
import {CarteraAction} from "../actions/cartera-action";
import FetchTipoOperacion = CarteraAction.FetchTipoOperacion;
import FetchCarteras = CarteraAction.FetchCarteras;
import UpsertCartera = CarteraAction.UpsertCartera;
import RemoveCartera = CarteraAction.RemoveCartera;
import FetchFuentes = CarteraAction.FetchFuentes;
import FetchColumnas = CarteraAction.FetchColumnas;
import CloneCartera = CarteraAction.CloneCartera;
import FetchTags = CarteraAction.FetchTags;
import FetchBrokers = CarteraAction.FetchBrokers;
import FetchTipoVencimiento = CarteraAction.FetchTipoVencimiento;
import FetchOperacionesSimbolo = CarteraAction.FetchOperacionesSimbolo;
import FetchPagination = CarteraAction.FetchPaginationCartera;
import { CarteraService } from '../services/cartera.service';

@State<CarteraStateInterface>({
  name: 'carteraData',
  defaults: {
    tiposOperacion: [],
    fuentes: [],
    carteras: [],
    carteraColumnas: [],
    operacionColumnas: [],
    tags: [],
    brokers: [],
    tiposVencimiento: [],
    operacionesSimbolo: [],
    pagination: []
  }
})
@Injectable()
export class CarteraState {

  constructor(private store: Store, private requestService: RequestService, private carteraService: CarteraService) {
  }

  @Selector()
  static carteras(state: CarteraStateInterface) {

    return JSON.parse(JSON.stringify(state.carteras));
  }

  @Selector()
  static carteraColumnas(state: CarteraStateInterface) {

    return state.carteraColumnas;
  }

  @Selector()
  static operacionColumna(state: CarteraStateInterface) {

    return state.operacionColumnas;
  }

  @Selector()
  static tags(state: CarteraStateInterface) {

    return state.tags;
  }

  @Selector()
  static operacionesSimbolo(state: CarteraStateInterface) {

    return JSON.parse(JSON.stringify(state.operacionesSimbolo));
  }

  @Selector()
  static pagination(state: CarteraStateInterface) {

    return JSON.parse(JSON.stringify(state.pagination));
  }

  @Action(FetchTipoOperacion)
  async fetchTiposOperacion({getState, setState}: StateContext<CarteraStateInterface>){

    let data = [];
    try {

      data = await this.requestService.get('tipo_operacion_index',
        { hideLoading: true, params: { } });

    } catch (e) {

      AppHelper.DevPrint(e);
    }

    setState({ ...getState(), ... { tiposOperacion: data } });
  }

  @Action(FetchFuentes)
  async fetchFuentes({getState, setState}: StateContext<CarteraStateInterface>){

    let data = [];
    try {

      data = await this.requestService.get('fuente_index',
        { hideLoading: true, params: { } });

    } catch (e) {

      AppHelper.DevPrint(e);
    }

    setState({ ...getState(), ... { fuentes: data } });
  }

  @Action(FetchBrokers)
  async fetchBrokers({getState, setState}: StateContext<CarteraStateInterface>){

    let data = [];
    try {

      data = await this.requestService.get('broker_index',
        { hideLoading: true, params: { } });

    } catch (e) {

      AppHelper.DevPrint(e);
    }

    setState({ ...getState(), ... { brokers: data } });
  }

  @Action(FetchTipoVencimiento)
  async fetchTipoVencimiento({getState, setState}: StateContext<CarteraStateInterface>){

    let data = [];
    try {

      data = await this.requestService.get('tipo_vencimiento_index',
        { hideLoading: true, params: { } });

    } catch (e) {

      AppHelper.DevPrint(e);
    }

    setState({ ...getState(), ... { tiposVencimiento: data } });
  }

  @Action(FetchColumnas)
  async fetchColumnas({getState, setState}: StateContext<CarteraStateInterface>, action: FetchColumnas){

    let data = [];
    try {

      data = await this.requestService.get(`${action.tipo}_columna_index`,
        { hideLoading: true, params: { } });

    } catch (e) {

      AppHelper.DevPrint(e);
    }

    setState({ ...getState(), ... { [`${action.tipo}Columnas`]: data } });
  }

  @Action(FetchCarteras)
  async fetchCarteras({getState, setState}: StateContext<CarteraStateInterface>, action: FetchCarteras){

    let data = [];
    try {

      let query = `exists[deletedAt]=0`;

      if (action.id) {

        query += `&id=${action.id}`;
      }

      data = await this.requestService.get('cartera_index',
        { hideLoading: action.hideLoading, params: { }, query });

      data.forEach(c => {

        c.precioTotal = AppHelper.FormatNumber(c.precioTotal || 0, this.carteraService.locale,
            2, 'currency', c.moneda);
        AppHelper.FormatFromColumn( c, getState().carteraColumnas, this.carteraService.locale );
      });

    } catch (e) {

      AppHelper.DevPrint(e);
    }

    setState({ ...getState(), ... { carteras: data } });
  }

  @Action(UpsertCartera)
  async upsertCartera({getState, setState}: StateContext<CarteraStateInterface>, action: UpsertCartera){

    const state = getState();
    try {

      const carteras = JSON.parse(JSON.stringify(state.carteras));

      const index = carteras.findIndex(c => c.id === action.cartera?.id);

      if (!action.cartera?.id || index < 0) {

        carteras.push(await this.requestService.post('cartera_index', {
          params: {
            nombre: action.cartera.nombre,
            moneda: action.cartera.moneda || 'EUR'
          }
        }));
      } else {

        carteras[index] = await this.requestService.put('cartera_details',
          { hideLoading: true,
            params: { ... carteras[index], ...action.cartera } });
      }

      setState({ ...getState(), ... { carteras: carteras } });
    } catch (e) {

      AppHelper.DevPrint(e);
    }
  }

  @Action(RemoveCartera)
  async removeCartera({getState, setState}: StateContext<CarteraStateInterface>, action: RemoveCartera){

    const state = getState();
    try {

      const carteras = JSON.parse(JSON.stringify(state.carteras));

      const index = carteras.findIndex(c => c.id === action.id);

      if (index >= 0) {

        await this.requestService.delete('cartera_details', { hideLoading: false, params: { id: action.id } });
        carteras.splice(index, 1);
        setState({ ...getState(), ... { carteras: carteras } });
      }

    } catch (e) {

      AppHelper.DevPrint(e);
    }
  }

  @Action(CloneCartera)
  async cloneCartera({getState, setState}: StateContext<CarteraStateInterface>, action: CloneCartera){

    const state = getState();
    try {

      const carteras = JSON.parse(JSON.stringify(state.carteras));

      carteras.push(await this.requestService.post('cartera_clone',
        { params: { id: action.id } }));

        setState({ ...getState(), ... { carteras: carteras } });
    } catch (e) {

      AppHelper.DevPrint(e);
    }
  }

  @Action(FetchTags)
  async fetchTags({getState, setState}: StateContext<CarteraStateInterface>){

    let data = [];
    try {

      data = await this.requestService.get('tags_index',
        { hideLoading: true, params: { }, query: 'pageable=false' });

    } catch (e) {

      AppHelper.DevPrint(e);
    }

    setState({ ...getState(), ... { tags: data.map(t => {

        return {
          cartera: t.cartera,
          nombre: t.nombre
        }
      })
    } } );
  }

  //Set simbolos ha actualizar
  @Action(FetchOperacionesSimbolo)
  async fetchOperacionesSimbolo({getState, patchState}: StateContext<CarteraStateInterface>, action: FetchOperacionesSimbolo){

    let content: any = { };

    let data: any = { };
    
    try {

      content = await this.carteraService.obtenerSimbolos(action.id, action.hideLoading);

    } catch (e) {

      AppHelper.DevPrint(e);
    }

    data = {...content};

    data.data = [];

   // setState({ ...getState(), ... { operacionesSimbolo: content.data } } );
    patchState({ ...getState(), ... { operacionesSimbolo: content.data } } );
    //console.log('Datos escrito');

   // this.store.dispatch( new FetchPagination(data));

    //this.fetchPagination(this.getState(),content);
  }

  // @Action(FetchPagination)
  // async fetchPagination({getState, patchState}: StateContext<CarteraStateInterface>, action: FetchPagination){

  //   // let content = {data: []};
  //   // try {

  //   //   content = await this.carteraService.obtenerSimbolos(action.id, action.hideLoading);

  //   //   this.

  //   // } catch (e) {

  //   //   AppHelper.DevPrint(e);
  //   // }

  //   patchState({ ...getState(), ... action } );
  // }
}
